import "./mobileEventList.css";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useAppContext } from "../../../context/AppContext.js";
import {
	DateToggleControl,
	MobileEventCard,
	Modal,
} from "../../../components/index.js";
import {
	ViewEvent,
	EditEvent,
	SuccessModal,
} from "../../../components/Modal/index.js";
import { Skeleton } from "@mui/material";
import dataSkeleton from "./mobileSkeleton.json";
import { ToastContainer } from "react-toastify";
import { sort } from "../../../assets/index.js";

function MobileEventList() {
	const location = useLocation();
	const { mobileView, allEventData, loadingAllEvents } = useAppContext();
	const [view, setView] = useState("Event List");
	const [dates, setDates] = useState([]);
	const currentDate = moment().format("DD");
	const [showModal, setShowModal] = useState(false);
	const [modalContent, setModalContent] = useState(null);
	const [eventDateHeader, setEventDateHeader] = useState([]);
	const [filteredEvents, setFilteredEvents] = useState([]);

	// Get the days of the week:
	useEffect(() => {
		const startOfWeek = moment().startOf("week");
		const currentMonth = startOfWeek.clone().format("MMM");
		const currentYear = startOfWeek.clone().format("YYYY");

		const convertedArray = [...Array(7)].map((_, index) => {
			const day = startOfWeek.clone().add(index, "days");
			const formattedDay = day.format("dd DD");
			const [abbrev, date] = [
				formattedDay.substring(0, 1),
				formattedDay.substr(2),
			];

			return { day: abbrev, date, month: currentMonth, year: currentYear };
		});

		setEventDateHeader(convertedArray);
	}, []);

	// Handle date format for header:
	const handleEventDateFormat = useCallback(() => {
		const today = moment().format("YYYY/MM/DD");
		const eventsForCurrentData = allEventData;
		const todayEvent = eventsForCurrentData?.filter((event) => {
			return moment(event?.startTime).utc().format("YYYY/MM/DD") === today;
		});

		const formatted = todayEvent?.map((event) => ({
			startTime: moment(event?.startTime).utc().format("HH:mm"),
			endTime: moment(event?.endTime).utc().format("HH:mm"),
			date: moment(event?.startTime).utc().format("YYYY/MM/DD"),
		}));

		setDates(formatted);
		setFilteredEvents(todayEvent);
	}, [allEventData]);

	useEffect(() => {
		handleEventDateFormat();
	}, [handleEventDateFormat]);

	// Handle Change Day:
	const handleChangeDay = (data) => {
		const day = data.date;
		const month = data.month;
		const year = data.year;

		const selectedDate = moment(
			`${day} ${month} ${year}`,
			"DD MMM YYYY"
		).format("YYYY/MM/DD");

		const today = selectedDate;
		const eventsForCurrentData = allEventData;
		const todayEvent = eventsForCurrentData?.filter((event) => {
			return moment(event?.startTime).utc().format("YYYY/MM/DD") === today;
		});

		const formatted = todayEvent?.map((event) => ({
			startTime: moment(event?.startTime).utc().format("HH:mm"),
			endTime: moment(event?.endTime).utc().format("HH:mm"),
			date: moment(event?.startTime).utc().format("YYYY/MM/DD"),
		}));

		setDates(formatted);
		setFilteredEvents(todayEvent);
	};

	// Handle View Event:
	const handleViewEvent = (event) => {
		setShowModal(true);
		setModalContent(
			<ViewEvent
				event={event}
				title="View Event"
				onClose={() => setShowModal(false)}
				handleEditEvent={handleEditEvent}
			/>
		);
	};

	// Handle Edit Event:
	const handleEditEvent = (event) => {
		setShowModal(true);
		setModalContent(
			<EditEvent
				event={event}
				title="Edit Event"
				onClose={() => setShowModal(false)}
				handleShowSuccessModal={handleShowSuccessModal}
			/>
		);
	};

	// Handle Show Success Modal:
	const handleShowSuccessModal = () => {
		setShowModal(true);
		setModalContent(
			<SuccessModal
				mode="fromEdit"
				title="Event Saved"
				message="Event has been saved successfully"
				onClose={() => setShowModal(false)}
			/>
		);
	};

	// Side Effect | Event List:
	useEffect(() => {
		if (location?.pathname === "/dashboard/event-list") {
			setView("Event List");
		}

		document.title = "ED | Event List";
	}, [location.pathname]);

	return (
		<>
			<section className="event_list">
				<div className="event_list_container">
					{/* Header */}
					<DateToggleControl view={view} data={allEventData} />

					{/* Content */}
					<div className="event_list_content">
						<div className="event_list_content_headers mobile">
							{eventDateHeader?.map((data, index) => {
								return (
									<span
										key={index}
										className={`event_list_content_header_day mobile ${
											+currentDate === +data?.date
										}`}
										onClick={() => handleChangeDay(data)}
									>
										<p>{data?.day}</p>
										<p>{data?.date}</p>
									</span>
								);
							})}
						</div>

						{/* Content Body Header */}
						<div>
							{mobileView ? (
								<div className="event_list_content_info_header">
									<div className="event_list_content_info_header_time_div">
										<p>Time</p>
									</div>
									<div className="event_list_content_info_header_event_div">
										<p>Event List</p>
										<img src={sort} alt="sort" />
									</div>
								</div>
							) : null}
						</div>

						{/* Content Body */}
						{!loadingAllEvents ? (
							<div className="event_list_content_info">
								{filteredEvents?.length ? (
									<>
										<div className="event_list_content_info_events">
											<div className="event_list_content_info_date">
												{dates?.map((data, index) => {
													return (
														<span key={index}>
															<p>{data?.startTime}</p>
															<p>{data?.endTime}</p>
														</span>
													);
												})}
											</div>

											<div className="event_list_content_info_body">
												{filteredEvents.map((event) => {
													return (
														<MobileEventCard
															event={event}
															key={event?.id}
															handleViewEvent={handleViewEvent}
														/>
													);
												})}
											</div>
										</div>
									</>
								) : (
									<>
										<div className="event_list_content_info_events">
											<div className="event_list_content_info_date">
												<span>
													<p>00:00</p>
													<p>00:00</p>
												</span>
											</div>

											<div className="event_list_content_info_body">
												<div className="no_event_event_card">
													No event available
												</div>
											</div>
										</div>
									</>
								)}
							</div>
						) : (
							<div className="event_list_content_info">
								<div className="event_list_content_info_events">
									<div className="event_list_content_info_date">
										{dataSkeleton?.map((index) => {
											return (
												<span key={index}>
													<Skeleton
														key={index}
														animation="wave"
														variant="rectangular"
														className="event_list_content_info_date_skeleton_span"
													/>
													<Skeleton
														key={index}
														animation="wave"
														variant="rectangular"
														className="event_list_content_info_date_skeleton_span"
													/>
												</span>
											);
										})}
									</div>

									<div className="event_list_content_info_body">
										{dataSkeleton.map((index) => {
											return (
												<Skeleton
													key={index}
													animation="wave"
													variant="rectangular"
													className="event_list_content_info_body_skeleton"
												/>
											);
										})}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</section>

			{/* Modal */}
			{showModal && <Modal modalContent={modalContent} />}

			<ToastContainer />
		</>
	);
}

export default MobileEventList;
